import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import Sidebar from "../Layout/Sidebar";
import Header from "../Layout/Header";
import Swal from "sweetalert2";
import axios from "axios";
import "./AddTenantNda.css";
import DocumentTabs from "../TenantDocument/DocumentTabs";
import { API_BASE } from "../../../ApiUrl";

const API_URL = `${API_BASE}/tenant-ndas/`;
const TENANT_RECORDS_API_URL = `${API_BASE}/tenant-records/`;

const emptyNda = {
  id: null,
  version: "",
  parties: [],
  signatories: [],
  effective_date: "",
  expiry_date: "",
  evidence_document: "",
  // transient inputs for this row
  party_input: "",
  signatory_input: ""
};

const toDateInput = (v) => (v ? String(v).substring(0, 10) : "");

const AddTenantNda = () => {
  const navigate = useNavigate();

  const [ndas, setNdas] = useState([]);
  const [loading, setLoading] = useState(false);

  const [showForm, setShowForm] = useState(false);
  const [isEditing, setIsEditing] = useState(false);
  const [editingId, setEditingId] = useState(null);

  const [formData, setFormData] = useState([{ ...emptyNda }]);
  const [tenantId, setTenantId] = useState(null);

  const [documentsList, setDocumentsList] = useState([]);
  const [loadingDocuments, setLoadingDocuments] = useState(false);

  const getTenantUser = () => {
    try {
      const tenantUser = localStorage.getItem("tenant_user");
      return tenantUser ? JSON.parse(tenantUser) : null;
    } catch (error) {
      console.error("Error reading tenant_user:", error);
      return null;
    }
  };

  // ---- Get tenant id ----
  useEffect(() => {
    const tenantUser = getTenantUser();
    if (tenantUser?.tenant_id) setTenantId(Number(tenantUser.tenant_id));
  }, []);

  // ---- Fetch NDAs ----
  useEffect(() => {
    if (tenantId) fetchNdas();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tenantId]);

  const fetchNdas = async () => {
    try {
      setLoading(true);

      const response = await axios.post(
        TENANT_RECORDS_API_URL,
        {
          tenant_id: tenantId,
          tables: ["tenant_ndas"]
        },
        { headers: { "Content-Type": "application/json" } }
      );

      const result = response.data;

      let rows = [];
      if (result?.data?.tenant_ndas) {
        rows = result.data.tenant_ndas;
      } else if (result?.tenant_ndas) {
        rows = result.tenant_ndas;
      } else if (Array.isArray(result?.data)) {
        rows = result.data;
      } else if (Array.isArray(result)) {
        rows = result;
      }

      setNdas(rows);
    } catch (error) {
      console.error("Error fetching NDAs:", error);
      Swal.fire({
        icon: "error",
        title: "Unable to load NDAs",
        text:
          error.response?.data?.detail ||
          "Something went wrong while loading NDAs."
      });
    } finally {
      setLoading(false);
    }
  };

  // ---- Fetch tenant documents for the evidence dropdown ----
  useEffect(() => {
    const fetchDocuments = async () => {
      if (!tenantId) return;
      setLoadingDocuments(true);

      try {
        const response = await axios.post(
          TENANT_RECORDS_API_URL,
          {
            tenant_id: tenantId,
            tables: ["tenant_documents"]
          },
          { headers: { "Content-Type": "application/json" } }
        );

        const result = response.data;

        let rows = [];
        if (result?.data?.tenant_documents) {
          rows = result.data.tenant_documents;
        } else if (result?.tenant_documents) {
          rows = result.tenant_documents;
        } else if (Array.isArray(result?.data)) {
          rows = result.data;
        } else if (Array.isArray(result)) {
          rows = result;
        }

        setDocumentsList(rows);
      } catch (error) {
        console.error("Error fetching tenant documents:", error);
        setDocumentsList([]);
      } finally {
        setLoadingDocuments(false);
      }
    };

    fetchDocuments();
  }, [tenantId]);

  // ---- Form handlers (per row) ----
  const handleChange = (index, e) => {
    const { name, value } = e.target;
    setFormData((prev) =>
      prev.map((n, i) => (i === index ? { ...n, [name]: value } : n))
    );
  };

  const handleRowInputChange = (index, field, value) => {
    setFormData((prev) =>
      prev.map((n, i) => (i === index ? { ...n, [field]: value } : n))
    );
  };

  const addParty = (index) => {
    setFormData((prev) =>
      prev.map((n, i) => {
        if (i !== index) return n;
        const value = (n.party_input || "").trim();
        if (!value) return { ...n, party_input: "" };
        if (n.parties.includes(value)) return { ...n, party_input: "" };
        return { ...n, parties: [...n.parties, value], party_input: "" };
      })
    );
  };

  const removeParty = (index, party) => {
    setFormData((prev) =>
      prev.map((n, i) =>
        i === index
          ? { ...n, parties: n.parties.filter((p) => p !== party) }
          : n
      )
    );
  };

  const addSignatory = (index) => {
    setFormData((prev) =>
      prev.map((n, i) => {
        if (i !== index) return n;
        const value = (n.signatory_input || "").trim();
        if (!value) return { ...n, signatory_input: "" };
        if (n.signatories.includes(value))
          return { ...n, signatory_input: "" };
        return {
          ...n,
          signatories: [...n.signatories, value],
          signatory_input: ""
        };
      })
    );
  };

  const removeSignatory = (index, signatory) => {
    setFormData((prev) =>
      prev.map((n, i) =>
        i === index
          ? {
              ...n,
              signatories: n.signatories.filter((s) => s !== signatory)
            }
          : n
      )
    );
  };

  const handlePartyKeyDown = (index, e) => {
    if (e.key === "Enter") {
      e.preventDefault();
      addParty(index);
    }
  };

  const handleSignatoryKeyDown = (index, e) => {
    if (e.key === "Enter") {
      e.preventDefault();
      addSignatory(index);
    }
  };

  // ---- Row handlers ----
  const handleAdd = () => {
    setFormData([{ ...emptyNda }]);
    setIsEditing(false);
    setEditingId(null);
    setShowForm(true);
  };

  const handleAddAnother = () => {
    setFormData((prev) => [...prev, { ...emptyNda }]);
  };

  const handleRemoveForm = (index) => {
    setFormData((prev) => prev.filter((_, i) => i !== index));
  };

  const handleEdit = (n) => {
    setFormData([
      {
        id: n.id,
        version: n.version || "",
        parties: Array.isArray(n.parties)
          ? n.parties
          : typeof n.parties === "string" && n.parties
          ? n.parties.split(",").map((s) => s.trim()).filter(Boolean)
          : [],
        signatories: Array.isArray(n.signatories)
          ? n.signatories
          : typeof n.signatories === "string" && n.signatories
          ? n.signatories.split(",").map((s) => s.trim()).filter(Boolean)
          : [],
        effective_date: toDateInput(n.effective_date),
        expiry_date: toDateInput(n.expiry_date),
        evidence_document: n.evidence_document
          ? String(n.evidence_document)
          : "",
        party_input: "",
        signatory_input: ""
      }
    ]);
    setEditingId(n.id);
    setIsEditing(true);
    setShowForm(true);
  };

  const handleDelete = async (n) => {
    const confirm = await Swal.fire({
      icon: "warning",
      title: "Delete NDA?",
      text: `Are you sure you want to delete NDA version "${
        n.version || "this record"
      }"?`,
      showCancelButton: true,
      confirmButtonText: "Yes, Delete",
      cancelButtonText: "Cancel",
      confirmButtonColor: "#dc2626"
    });

    if (!confirm.isConfirmed) return;

    try {
      await axios.delete(`${API_URL}${n.id}/`);

      Swal.fire({
        icon: "success",
        title: "Deleted",
        text: "NDA deleted successfully.",
        timer: 1500,
        showConfirmButton: false
      });

      fetchNdas();
    } catch (error) {
      console.error("Error deleting NDA:", error);
      Swal.fire({
        icon: "error",
        title: "Delete failed",
        text:
          error.response?.data?.detail || "Unable to delete the NDA."
      });
    }
  };

  const extractError = (error) => {
    if (error.response?.data) {
      const d = error.response.data;
      if (typeof d === "string") return d;
      if (d.detail) return d.detail;
      if (d.message) return d.message;
      if (d.errors) {
        return Object.entries(d.errors)
          .map(
            ([field, errors]) =>
              `${field}: ${Array.isArray(errors) ? errors.join(", ") : errors}`
          )
          .join(" | ");
      }
      return JSON.stringify(d);
    }
    return error.message || "Unknown error";
  };

  // ---- Validation per row ----
  const validateRow = (n, index) => {
    const label = `NDA ${index + 1}`;

    const isEmptyRow =
      !n.version.trim() &&
      n.parties.length === 0 &&
      n.signatories.length === 0 &&
      !n.id;
    if (isEmptyRow) return true;

    if (!n.version.trim()) {
      Swal.fire({
        icon: "warning",
        title: "Version Required",
        text: `Please provide a Version for ${label}.`
      });
      return false;
    }

    if (n.parties.length === 0) {
      Swal.fire({
        icon: "warning",
        title: "Parties Required",
        text: `Please add at least one party for ${label}.`
      });
      return false;
    }

    if (n.signatories.length === 0) {
      Swal.fire({
        icon: "warning",
        title: "Signatories Required",
        text: `Please add at least one signatory for ${label}.`
      });
      return false;
    }

    if (
      n.effective_date &&
      n.expiry_date &&
      n.expiry_date < n.effective_date
    ) {
      Swal.fire({
        icon: "warning",
        title: "Invalid Dates",
        text: `Expiry Date for ${label} cannot be before Effective Date.`
      });
      return false;
    }

    return true;
  };

  // ---- Submit ----
  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!tenantId) {
      Swal.fire({
        icon: "error",
        title: "Tenant ID missing",
        text: "Unable to identify the current tenant."
      });
      return;
    }

    // Commit pending tag inputs into arrays for every row
    const committed = formData.map((n) => {
      const parties = [...n.parties];
      const partyPending = (n.party_input || "").trim();
      if (partyPending && !parties.includes(partyPending)) {
        parties.push(partyPending);
      }

      const signatories = [...n.signatories];
      const signatoryPending = (n.signatory_input || "").trim();
      if (signatoryPending && !signatories.includes(signatoryPending)) {
        signatories.push(signatoryPending);
      }

      return {
        ...n,
        parties,
        signatories,
        party_input: "",
        signatory_input: ""
      };
    });

    setFormData(committed);

    for (let i = 0; i < committed.length; i++) {
      if (!validateRow(committed[i], i)) return;
    }

    const activeRows = committed.filter(
      (n) =>
        n.version.trim() ||
        n.parties.length ||
        n.signatories.length ||
        n.id
    );

    const CONFIG = {
      headers: { "Content-Type": "application/json" }
    };

    const buildPayload = (n) => ({
      version: n.version.trim(),
      parties: n.parties,
      signatories: n.signatories,
      effective_date: n.effective_date || null,
      expiry_date: n.expiry_date || null,
      evidence_document: n.evidence_document
        ? Number(n.evidence_document)
        : null,
      tenant: tenantId
    });

    try {
      setLoading(true);

      if (isEditing) {
        await axios.put(
          `${API_URL}${editingId}/`,
          buildPayload(committed[0]),
          CONFIG
        );

        await Swal.fire({
          icon: "success",
          title: "Updated Successfully",
          timer: 1500,
          showConfirmButton: false
        });
      } else {
        for (const n of activeRows) {
          await axios.post(API_URL, buildPayload(n), CONFIG);
        }

        await Swal.fire({
          icon: "success",
          title: "Created Successfully",
          text: `${activeRows.length} NDA${
            activeRows.length > 1 ? "s" : ""
          } created successfully.`,
          timer: 1500,
          showConfirmButton: false
        });
      }

      setShowForm(false);
      setIsEditing(false);
      setEditingId(null);
      setFormData([{ ...emptyNda }]);

      await fetchNdas();
    } catch (error) {
      console.error("Error saving NDA:", error);

      Swal.fire({
        icon: "error",
        title: isEditing ? "Update failed" : "Creation failed",
        text: extractError(error)
      });
    } finally {
      setLoading(false);
    }
  };

  const handleCancel = () => {
    setShowForm(false);
    setIsEditing(false);
    setEditingId(null);
    setFormData([{ ...emptyNda }]);
  };

  // ---- Lookup helpers ----
  const getPartiesArray = (n) => {
    if (Array.isArray(n.parties)) return n.parties;
    if (typeof n.parties === "string" && n.parties) {
      return n.parties.split(",").map((s) => s.trim()).filter(Boolean);
    }
    return [];
  };

  const getSignatoriesArray = (n) => {
    if (Array.isArray(n.signatories)) return n.signatories;
    if (typeof n.signatories === "string" && n.signatories) {
      return n.signatories.split(",").map((s) => s.trim()).filter(Boolean);
    }
    return [];
  };

  const getDocumentLabel = (doc) => {
    if (!doc) return "";

    const type = (doc.document_type || "").toString().replace(/_/g, " ");
    const fileName = doc.file ? String(doc.file).split("/").pop() : "";
    const code = doc.document_code || doc.code || "";

    if (fileName) {
      return type ? `${type} — ${fileName}` : fileName;
    }
    if (code) {
      return type ? `${type} — ${code}` : code;
    }
    return type || `Document #${doc.id}`;
  };

  const getEvidenceDocumentLabel = (id) => {
    if (!id) return "-";
    const match = documentsList.find(
      (d) => String(d.id) === String(id)
    );
    return match ? getDocumentLabel(match) : `Document #${id}`;
  };

  return (
    <div className="app-layout">
      <Sidebar />

      <div className="main-content">
        <Header />

        <div className="tenant-nda-page">
          <DocumentTabs />

          {!showForm ? (
            <>
              <div className="tenant-nda-page-header">
                <div>
                  <h2>Tenant NDAs</h2>
                </div>

                <button
                  type="button"
                  className="tenant-nda-add-top-btn"
                  onClick={handleAdd}
                >
                  + Add NDA
                </button>
              </div>

              <div className="tenant-nda-list-card">
                {loading ? (
                  <div className="tenant-nda-loading">Loading NDAs...</div>
                ) : ndas.length === 0 ? (
                  <div className="tenant-nda-empty">
                    <div className="tenant-nda-empty-icon">+</div>
                    <h3>No NDAs Found</h3>
                    <p>
                      There are no NDA records available for the current
                      tenant.
                    </p>
                    <button
                      type="button"
                      className="tenant-nda-empty-add-btn"
                      onClick={handleAdd}
                    >
                      + Add NDA
                    </button>
                  </div>
                ) : (
                  <div className="tenant-nda-table-wrapper">
                    <table className="tenant-nda-table">
                      <thead>
                        <tr>
                          <th>Version</th>
                          <th>Parties</th>
                          <th>Signatories</th>
                          <th>Effective Date</th>
                          <th>Expiry Date</th>
                          <th>Evidence Document</th>
                          <th>Actions</th>
                        </tr>
                      </thead>

                      <tbody>
                        {ndas.map((n) => (
                          <tr key={n.id}>
                            <td>
                              <span className="tenant-nda-version-pill">
                                {n.version || "-"}
                              </span>
                            </td>

                            <td>
                              <div className="tenant-nda-pill-list">
                                {getPartiesArray(n).length === 0 ? (
                                  <span className="tenant-nda-muted">-</span>
                                ) : (
                                  getPartiesArray(n).map((p, i) => (
                                    <span
                                      key={i}
                                      className="tenant-nda-pill"
                                    >
                                      {p}
                                    </span>
                                  ))
                                )}
                              </div>
                            </td>

                            <td>
                              <div className="tenant-nda-pill-list">
                                {getSignatoriesArray(n).length === 0 ? (
                                  <span className="tenant-nda-muted">-</span>
                                ) : (
                                  getSignatoriesArray(n).map((s, i) => (
                                    <span
                                      key={i}
                                      className="tenant-nda-pill"
                                    >
                                      {s}
                                    </span>
                                  ))
                                )}
                              </div>
                            </td>

                            <td className="tenant-nda-muted">
                              {n.effective_date || "-"}
                            </td>

                            <td className="tenant-nda-muted">
                              {n.expiry_date || "-"}
                            </td>

                            <td className="tenant-nda-muted">
                              {getEvidenceDocumentLabel(n.evidence_document)}
                            </td>

                            <td>
                              <div className="tenant-nda-actions">
                                <button
                                  type="button"
                                  className="tenant-nda-edit-btn"
                                  onClick={() => handleEdit(n)}
                                >
                                  Edit
                                </button>

                                <button
                                  type="button"
                                  className="tenant-nda-delete-btn"
                                  onClick={() => handleDelete(n)}
                                >
                                  Delete
                                </button>
                              </div>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                )}
              </div>
            </>
          ) : (
            <>
              <div className="tenant-nda-page-header">
                <div>
                  <h2>{isEditing ? "Edit NDA" : "Create NDAs"}</h2>
                </div>

                <button
                  type="button"
                  className="tenant-nda-back-btn"
                  onClick={handleCancel}
                >
                  ← Back
                </button>
              </div>

              <form onSubmit={handleSubmit}>
                {formData.map((n, index) => (
                  <div
                    className="tenant-nda-card"
                    key={index}
                    style={{ marginBottom: "20px" }}
                  >
                    <div className="tenant-nda-form-body">
                      <div className="tenant-nda-record-header">
                        <h4>NDA {index + 1}</h4>

                        {!isEditing && formData.length > 1 && (
                          <button
                            type="button"
                            className="tenant-nda-remove-btn"
                            onClick={() => handleRemoveForm(index)}
                          >
                            Remove
                          </button>
                        )}
                      </div>

                      <div className="tenant-nda-form-grid">
                        <div className="tenant-nda-form-group">
                          <label>
                            NDA Version <span>*</span>
                          </label>
                          <input
                            type="text"
                            name="version"
                            value={n.version}
                            onChange={(e) => handleChange(index, e)}
                            placeholder="e.g. 1.0"
                            maxLength={50}
                          />
                        </div>

                        <div className="tenant-nda-form-group">
                          <label>Effective Date</label>
                          <input
                            type="date"
                            name="effective_date"
                            value={n.effective_date}
                            onChange={(e) => handleChange(index, e)}
                          />
                        </div>

                        <div className="tenant-nda-form-group">
                          <label>Expiry Date</label>
                          <input
                            type="date"
                            name="expiry_date"
                            value={n.expiry_date}
                            onChange={(e) => handleChange(index, e)}
                            min={n.effective_date || undefined}
                          />
                        </div>
                      </div>

                      <div className="tenant-nda-tag-group">
                        <label>
                          NDA Parties <span>*</span>
                        </label>

                        <div className="tenant-nda-tag-input-wrapper">
                          <input
                            type="text"
                            value={n.party_input}
                            onChange={(e) =>
                              handleRowInputChange(
                                index,
                                "party_input",
                                e.target.value
                              )
                            }
                            onKeyDown={(e) => handlePartyKeyDown(index, e)}
                            placeholder="Enter party name and press Enter"
                          />

                          <button
                            type="button"
                            className="tenant-nda-add-item-btn"
                            onClick={() => addParty(index)}
                          >
                            Add
                          </button>
                        </div>

                        {n.parties.length > 0 && (
                          <div className="tenant-nda-tags">
                            {n.parties.map((party, i) => (
                              <div
                                className="tenant-nda-tag"
                                key={`${party}-${i}`}
                              >
                                <span>{party}</span>
                                <button
                                  type="button"
                                  onClick={() => removeParty(index, party)}
                                  aria-label={`Remove ${party}`}
                                >
                                  ×
                                </button>
                              </div>
                            ))}
                          </div>
                        )}
                      </div>

                      <div className="tenant-nda-tag-group">
                        <label>
                          Signatories <span>*</span>
                        </label>

                        <div className="tenant-nda-tag-input-wrapper">
                          <input
                            type="text"
                            value={n.signatory_input}
                            onChange={(e) =>
                              handleRowInputChange(
                                index,
                                "signatory_input",
                                e.target.value
                              )
                            }
                            onKeyDown={(e) =>
                              handleSignatoryKeyDown(index, e)
                            }
                            placeholder="Enter signatory name and press Enter"
                          />

                          <button
                            type="button"
                            className="tenant-nda-add-item-btn"
                            onClick={() => addSignatory(index)}
                          >
                            Add
                          </button>
                        </div>

                        {n.signatories.length > 0 && (
                          <div className="tenant-nda-tags">
                            {n.signatories.map((signatory, i) => (
                              <div
                                className="tenant-nda-tag"
                                key={`${signatory}-${i}`}
                              >
                                <span>{signatory}</span>
                                <button
                                  type="button"
                                  onClick={() =>
                                    removeSignatory(index, signatory)
                                  }
                                  aria-label={`Remove ${signatory}`}
                                >
                                  ×
                                </button>
                              </div>
                            ))}
                          </div>
                        )}
                      </div>

                      <div className="tenant-nda-document-box">
                        <div className="tenant-nda-document-content">
                          <label>Evidence Document</label>

                          <select
                            name="evidence_document"
                            value={n.evidence_document}
                            onChange={(e) => handleChange(index, e)}
                            disabled={loadingDocuments}
                          >
                            <option value="">
                              {loadingDocuments
                                ? "Loading documents..."
                                : "Select evidence document"}
                            </option>

                            {documentsList.map((doc) => (
                              <option key={doc.id} value={doc.id}>
                                {getDocumentLabel(doc)}
                              </option>
                            ))}
                          </select>
                        </div>
                      </div>
                    </div>
                  </div>
                ))}

                {!isEditing && (
                  <button
                    type="button"
                    className="tenant-nda-add-btn"
                    onClick={handleAddAnother}
                    style={{ marginBottom: "20px" }}
                  >
                    <span>+</span>
                    Add Another NDA
                  </button>
                )}

                <div className="tenant-nda-form-actions">
                  <button
                    type="button"
                    className="tenant-nda-cancel-btn"
                    onClick={handleCancel}
                    disabled={loading}
                  >
                    Cancel
                  </button>

                  <button
                    type="submit"
                    className="tenant-nda-save-btn"
                    disabled={loading}
                  >
                    {loading
                      ? "Saving..."
                      : isEditing
                      ? "Update NDA"
                      : "Save NDAs"}
                  </button>
                </div>
              </form>
            </>
          )}
        </div>
      </div>
    </div>
  );
};

export default AddTenantNda;